// Deals API service
import { baseClient } from './baseClient';
import type {
  Deal,
  DealCreate,
  DealUpdate,
  DealReadWithInsights,
  DealReadWithInsightsAndTasks,
  DealFilters,
  DealRoom,
  DealRoomCreate,
  DealRoomData,
  ChatMessage,
  ChatMessageCreate
} from '../types/api';

export class DealsApi {
  private static instance: DealsApi;

  private constructor() {}

  public static getInstance(): DealsApi {
    if (!DealsApi.instance) {
      DealsApi.instance = new DealsApi();
    }
    return DealsApi.instance;
  }

  /**
   * Get all deals with optional filtering
   */
  async getDeals(filters?: DealFilters): Promise<DealReadWithInsights[]> {
    const params = new URLSearchParams();
    
    if (filters?.q) params.append('q', filters.q);
    if (filters?.skip) params.append('skip', filters.skip.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.account_id) params.append('account_id', filters.account_id);
    if (filters?.stage) params.append('stage', filters.stage);
    if (filters?.owner_id) params.append('owner_id', filters.owner_id);

    const queryString = params.toString();
    const url = queryString ? `/deals?${queryString}` : '/deals';
    
    return baseClient.get<DealReadWithInsights[]>(url);
  }

  /**
   * Get a single deal by ID with insights and tasks
   */
  async getDeal(id: string): Promise<DealReadWithInsightsAndTasks> {
    return baseClient.get<DealReadWithInsightsAndTasks>(`/deals/${id}`);
  }

  /**
   * Create a new deal
   */
  async createDeal(deal: DealCreate): Promise<Deal> {
    return baseClient.post<Deal>('/deals', deal);
  }

  /**
   * Update an existing deal
   */
  async updateDeal(id: string, deal: DealUpdate): Promise<Deal> {
    return baseClient.patch<Deal>(`/deals/${id}`, deal);
  }

  /**
   * Delete a deal
   */
  async deleteDeal(id: string): Promise<void> {
    return baseClient.delete<void>(`/deals/${id}`);
  }

  // Deal Room functionality
  /**
   * Create a deal room for a deal
   */
  async createDealRoom(dealRoom: DealRoomCreate): Promise<DealRoom> {
    return baseClient.post<DealRoom>('/deal-rooms/', dealRoom);
  }

  /**
   * Get deal room data including deal, tasks, activities, and chat history
   */
  async getDealRoom(dealId: string): Promise<DealRoomData> {
    return baseClient.get<DealRoomData>(`/deals/${dealId}/room`);
  }

  /**
   * Get chat messages for a deal room
   */
  async getChatMessages(roomId: string, skip?: number, limit?: number): Promise<ChatMessage[]> {
    const params = new URLSearchParams();
    if (skip) params.append('skip', skip.toString());
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const url = queryString ? `/deal-rooms/${roomId}/messages?${queryString}` : `/deal-rooms/${roomId}/messages`;
    
    return baseClient.get<ChatMessage[]>(url);
  }

  /**
   * Send a chat message
   */
  async sendChatMessage(message: ChatMessageCreate): Promise<ChatMessage> {
    return baseClient.post<ChatMessage>('/chat-messages/', message);
  }

  /**
   * Get deals review queue (for managers)
   */
  async getDealReviewQueue(): Promise<any[]> {
    return baseClient.get<any[]>('/review-queue');
  }

  /**
   * Get deals by account
   */
  async getDealsByAccount(accountId: string): Promise<DealReadWithInsights[]> {
    return this.getDeals({ account_id: accountId });
  }

  /**
   * Get deals by stage
   */
  async getDealsByStage(stage: string): Promise<DealReadWithInsights[]> {
    return this.getDeals({ stage: stage as any });
  }

  /**
   * Get deals by owner
   */
  async getDealsByOwner(ownerId: string): Promise<DealReadWithInsights[]> {
    return this.getDeals({ owner_id: ownerId });
  }
}

// Export singleton instance
export const dealsApi = DealsApi.getInstance(); 